import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import {
  QueryClient,
  useQuery,
  useSuspenseQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type UseSuspenseQueryOptions,
  type UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { useAuth } from "./AuthHook";
import { DEFAULT_API_URL_V3 } from "../components/constants";
import * as GenesisTypes from "../types/genesisTypes";

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** Thrown on HTTP 422 when the body matches `ApiErrorResponse`. Plain `Error` with a stable `name` + `errors` payload (no `class` / `super`). */
export type GenesisValidationError = Error & {
  name: "GenesisValidationError";
  errors: Record<string, string[]>;
};

const GENESIS_VALIDATION_ERROR_NAME = "GenesisValidationError" as const;

export function createGenesisValidationError(
  message: string,
  errors: Record<string, string[]>,
): GenesisValidationError {
  const err = new Error(message) as GenesisValidationError;
  err.name = GENESIS_VALIDATION_ERROR_NAME;
  err.errors = errors;
  return err;
}

export function isGenesisValidationError(
  e: unknown,
): e is GenesisValidationError {
  if (!(e instanceof Error) || e.name !== GENESIS_VALIDATION_ERROR_NAME) {
    return false;
  }
  const rec = e as Error & { errors?: unknown };
  if (!isRecord(rec.errors)) return false;
  return Object.values(rec.errors).every(
    (arr) => Array.isArray(arr) && arr.every((m) => typeof m === "string"),
  );
}

function isApiErrorResponse(v: unknown): v is GenesisTypes.ApiErrorResponse {
  if (!isRecord(v) || !isRecord(v.errors)) return false;
  return Object.values(v.errors).every(
    (arr) => Array.isArray(arr) && arr.every((m) => typeof m === "string"),
  );
}

function isDetailErrorResponse(
  v: unknown,
): v is GenesisTypes.PhotoPresignErrorResponse {
  return (
    isRecord(v) && isRecord(v.errors) && typeof v.errors.detail === "string"
  );
}

function genesisOrigin(base: string): string {
  return base.replace(/\/+$/, "");
}

function genesisUrl(origin: string, path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${genesisOrigin(origin)}${p}`;
}

function requireGenesisOrigin(raw: unknown): string {
  if (typeof raw !== "string" || raw.trim() === "") {
    throw new Error(
      "VITE_APP_GENESIS_API_URL is missing or empty. Set it in .env (e.g. .env.development) and in CI build env for production.",
    );
  }
  return raw.trim();
}

async function throwFromResponse(res: Response, url: string): Promise<never> {
  const text = await res.text();
  let message = res.statusText || `${res.status}`;

  if (text) {
    try {
      const body = JSON.parse(text) as unknown;
      if (res.status === 422 && isApiErrorResponse(body)) {
        const flat = Object.values(body.errors)
          .flat()
          .filter((s) => typeof s === "string" && s.length > 0);
        throw createGenesisValidationError(
          flat.length > 0 ? flat.join("; ") : "Validation failed",
          body.errors,
        );
      }
      // Presign endpoint returns { errors: { detail: string } } on 422.
      if (res.status === 422 && isDetailErrorResponse(body)) {
        throw createGenesisValidationError(body.errors.detail, {
          detail: [body.errors.detail],
        });
      }
      if (isRecord(body)) {
        if (typeof body.message === "string") message = body.message;
        else if (typeof body.error === "string") message = body.error;
        else if (typeof body.detail === "string") message = body.detail;
        else message = text;
      } else {
        message = text;
      }
    } catch (e) {
      if (isGenesisValidationError(e)) throw e;
      message = text;
    }
  }

  throw new Error(`Genesis request failed ${url}: ${message}`);
}

type FactorySuspenseQueryOptions<T> = Omit<
  UseSuspenseQueryOptions<T, Error>,
  "queryKey" | "queryFn"
>;
type FactoryQueryOptions<T> = Omit<
  UseQueryOptions<T, Error>,
  "queryKey" | "queryFn"
>;

interface QueryFactory<TArgSchema, TData> {
  useSuspenseQuery: (
    args: TArgSchema,
    opts?: FactorySuspenseQueryOptions<TData>,
  ) => UseSuspenseQueryResult<TData, Error>;
  useQuery: (
    args: TArgSchema,
    opts?: FactoryQueryOptions<TData>,
  ) => UseQueryResult<TData, Error>;
  invalidate: (queryClient: QueryClient, args: TArgSchema) => Promise<void>;
}

/** Stable cache key is the resolved URL only (`queryFn` uses nothing else). Args are not in the key so equivalent URLs share one entry. */
function queryFactory<TArgs, TData>(
  url: string | ((args: TArgs) => string),
  queryFn: (url: string) => Promise<TData>,
): QueryFactory<TArgs, TData> {
  const resolveUrl = typeof url === "string" ? () => url : url;
  return {
    useSuspenseQuery: (args, opts) => {
      const resolved = resolveUrl(args);
      return useSuspenseQuery({
        ...opts,
        queryKey: ["genesis", resolved],
        queryFn: () => queryFn(resolved),
      });
    },
    useQuery: (args, opts) => {
      const resolved = resolveUrl(args);
      return useQuery({
        ...opts,
        queryKey: ["genesis", resolved],
        queryFn: () => queryFn(resolved),
      });
    },
    invalidate: (queryClient, args) => {
      const resolved = resolveUrl(args);
      return queryClient.invalidateQueries({
        queryKey: ["genesis", resolved],
      });
    },
  };
}

type SiteFilterArgs = { site_mongo_id?: string | null };

/** 1-based page + size shared by the paginated list endpoints. */
type PaginationArgs = {
  page?: number | null;
  page_size?: number | null;
};

/** Site scopes hold only a handful of distributors; fetch them in one page. */
export const SITE_DISTRIBUTORS_PAGE_SIZE = 100;

/** Filters for the trip index and the flat note search. */
type TripListArgs = PaginationArgs & {
  site_mongo_id?: string | null;
  from?: string | null;
  to?: string | null;
};

type NoteListArgs = PaginationArgs & {
  site_mongo_id?: string | null;
  issue_type_ids?: string[] | null;
  well_mongo_ids?: string[] | null;
  has_distributor?: boolean | null;
  from?: string | null;
  to?: string | null;
};

/** Filters for the distributor index. */
type DistributorListArgs = PaginationArgs & {
  site_mongo_id?: string | null;
  injection_type_id?: string | null;
  water_type_id?: string | null;
  power_type_id?: string | null;
};

function appendPaginationParams(u: URL, args: PaginationArgs): void {
  if (args.page != null) u.searchParams.set("page", String(args.page));
  if (args.page_size != null)
    u.searchParams.set("page_size", String(args.page_size));
}

type DeploymentsArgs = { since: string; until?: string };

function deploymentsListUrl(origin: string, args: DeploymentsArgs): string {
  const u = new URL(genesisUrl(origin, "/api/reports/deployments"));
  u.searchParams.set("since", args.since);
  if (args.until) u.searchParams.set("until", args.until);
  return u.toString();
}

type LookupSegment =
  | "well-types"
  | "well-power-types"
  | "issue-types"
  | "distributor-injection-types"
  | "distributor-water-types";

function fieldNotesListUrl(origin: string, args: NoteListArgs): string {
  const path = genesisUrl(origin, "/api/field/notes");
  const u = new URL(path);
  if (args.site_mongo_id)
    u.searchParams.set("site_mongo_id", args.site_mongo_id);
  if (args.issue_type_ids && args.issue_type_ids.length > 0) {
    u.searchParams.set("issue_type_ids", args.issue_type_ids.join(","));
  }
  if (args.well_mongo_ids && args.well_mongo_ids.length > 0) {
    u.searchParams.set("well_mongo_ids", args.well_mongo_ids.join(","));
  }
  if (args.has_distributor != null) {
    u.searchParams.set("has_distributor", String(args.has_distributor));
  }
  if (args.from) u.searchParams.set("from", args.from);
  if (args.to) u.searchParams.set("to", args.to);
  appendPaginationParams(u, args);
  return u.searchParams.toString() ? u.toString() : path;
}

function distributorsListUrl(
  origin: string,
  args: DistributorListArgs,
): string {
  const path = genesisUrl(origin, "/api/field/distributors");
  const u = new URL(path);
  if (args.site_mongo_id)
    u.searchParams.set("site_mongo_id", args.site_mongo_id);
  if (args.injection_type_id) {
    u.searchParams.set("injection_type_id", args.injection_type_id);
  }
  if (args.water_type_id)
    u.searchParams.set("water_type_id", args.water_type_id);
  if (args.power_type_id)
    u.searchParams.set("power_type_id", args.power_type_id);
  appendPaginationParams(u, args);
  return u.searchParams.toString() ? u.toString() : path;
}

function fieldTripsListUrl(origin: string, args: TripListArgs): string {
  const path = genesisUrl(origin, "/api/field/trips");
  const u = new URL(path);
  if (args.site_mongo_id)
    u.searchParams.set("site_mongo_id", args.site_mongo_id);
  if (args.from) u.searchParams.set("from", args.from);
  if (args.to) u.searchParams.set("to", args.to);
  appendPaginationParams(u, args);
  return u.searchParams.toString() ? u.toString() : path;
}

function wellInfosListUrl(origin: string, args: SiteFilterArgs): string {
  const path = genesisUrl(origin, "/api/field/well-infos");
  if (!args.site_mongo_id) return path;
  const u = new URL(path);
  u.searchParams.set("site_mongo_id", args.site_mongo_id);
  return u.toString();
}

function buildLookupSegment(
  origin: string,
  segment: LookupSegment,
  genesisGetJson: (url: string) => Promise<unknown>,
  genesisPostJson: (url: string, body: unknown) => Promise<unknown>,
  genesisPutJson: (url: string, body: unknown) => Promise<unknown>,
  genesisDelete204: (url: string) => Promise<void>,
): LookupResource {
  const basePath = `/api/field/${segment}`;
  const baseUrl = genesisUrl(origin, basePath);
  return {
    list: queryFactory<void, GenesisTypes.FieldLookupRecordJson[]>(
      baseUrl,
      async (url) =>
        ((await genesisGetJson(url)) as GenesisTypes.FieldLookupIndexResponse)
          .data,
    ),
    show: queryFactory<{ id: string }, GenesisTypes.FieldLookupRecordJson>(
      ({ id }) => `${baseUrl}/${encodeURIComponent(id)}`,
      async (url) =>
        ((await genesisGetJson(url)) as GenesisTypes.FieldLookupShowResponse)
          .data,
    ),
    create: async (body: GenesisTypes.FieldLookupWriteBody) =>
      (
        (await genesisPostJson(
          baseUrl,
          body,
        )) as GenesisTypes.FieldLookupShowResponse
      ).data,
    update: async (id: string, body: GenesisTypes.FieldLookupWriteBody) =>
      (
        (await genesisPutJson(
          `${baseUrl}/${encodeURIComponent(id)}`,
          body,
        )) as GenesisTypes.FieldLookupShowResponse
      ).data,
    delete: (id: string) =>
      genesisDelete204(`${baseUrl}/${encodeURIComponent(id)}`),
  };
}

interface LookupResource {
  list: QueryFactory<void, GenesisTypes.FieldLookupRecordJson[]>;
  show: QueryFactory<{ id: string }, GenesisTypes.FieldLookupRecordJson>;
  create: (
    body: GenesisTypes.FieldLookupWriteBody,
  ) => Promise<GenesisTypes.FieldLookupRecordJson>;
  update: (
    id: string,
    body: GenesisTypes.FieldLookupWriteBody,
  ) => Promise<GenesisTypes.FieldLookupRecordJson>;
  delete: (id: string) => Promise<void>;
}

interface FieldNotesResource {
  list: QueryFactory<NoteListArgs, GenesisTypes.FieldNoteListResult>;
  show: QueryFactory<{ id: string }, GenesisTypes.FieldNoteJson>;
  create: (
    body: GenesisTypes.FieldNoteCreateBody,
  ) => Promise<GenesisTypes.FieldNoteJson>;
  update: (
    id: string,
    body: GenesisTypes.FieldNoteUpdateBody,
  ) => Promise<GenesisTypes.FieldNoteJson>;
  delete: (id: string) => Promise<void>;
}

interface FieldTripsResource {
  list: QueryFactory<TripListArgs, GenesisTypes.FieldTripListResult>;
  show: QueryFactory<{ id: string }, GenesisTypes.FieldTripJson>;
  create: (
    body: GenesisTypes.FieldTripCreateBody,
  ) => Promise<GenesisTypes.FieldTripJson>;
  update: (
    id: string,
    body: GenesisTypes.FieldTripUpdateBody,
  ) => Promise<GenesisTypes.FieldTripJson>;
  delete: (id: string) => Promise<void>;
}

interface PhotosResource {
  /** Request a presigned direct-to-S3 upload URL + persisted S3 key. */
  presign: (
    body: GenesisTypes.PhotoPresignBody,
  ) => Promise<GenesisTypes.PhotoPresignData>;
  /**
   * Presign + upload a single file directly to S3, resolving to the S3 key to
   * persist on the trip/note `photo_paths`. The S3 PUT sends `Content-Type`
   * exactly equal to the presigned type.
   */
  upload: (
    file: File,
    scope: GenesisTypes.PhotoScope,
    siteMongoId: string,
  ) => Promise<string>;
}

interface FieldWellInfosResource {
  list: QueryFactory<SiteFilterArgs, GenesisTypes.FieldWellInfoJson[]>;
  show: QueryFactory<{ id: string }, GenesisTypes.FieldWellInfoJson>;
  create: (
    body: GenesisTypes.FieldWellInfoCreateBody,
  ) => Promise<GenesisTypes.FieldWellInfoJson>;
  update: (
    id: string,
    body: GenesisTypes.FieldWellInfoUpdateBody,
  ) => Promise<GenesisTypes.FieldWellInfoJson>;
  delete: (id: string) => Promise<void>;
}

interface DistributorParametersResource {
  list: QueryFactory<
    { distributorId: string },
    GenesisTypes.DistributorParameterJson[]
  >;
  show: QueryFactory<
    { distributorId: string; id: string },
    GenesisTypes.DistributorParameterJson
  >;
  create: (
    distributorId: string,
    body: GenesisTypes.DistributorParameterWriteBody,
  ) => Promise<GenesisTypes.DistributorParameterJson>;
  update: (
    distributorId: string,
    id: string,
    body: GenesisTypes.DistributorParameterWriteBody,
  ) => Promise<GenesisTypes.DistributorParameterJson>;
  delete: (distributorId: string, id: string) => Promise<void>;
}

interface DistributorsResource {
  list: QueryFactory<DistributorListArgs, GenesisTypes.DistributorListResult>;
  show: QueryFactory<{ id: string }, GenesisTypes.DistributorJson>;
  create: (
    body: GenesisTypes.DistributorWriteBody,
  ) => Promise<GenesisTypes.DistributorJson>;
  update: (
    id: string,
    body: Partial<GenesisTypes.DistributorWriteBody>,
  ) => Promise<GenesisTypes.DistributorJson>;
  delete: (id: string) => Promise<void>;
  parameters: DistributorParametersResource;
}

interface LabelsResource {
  find: (name: string) => Promise<GenesisTypes.LabelFindData>;
}

interface GenesisQuantifiersResource {
  create: (
    siteId: string,
    body: GenesisTypes.GenesisCreateQuantifierBody,
  ) => Promise<GenesisTypes.GenesisQuantifierData>;
}

interface DeploymentsResource {
  list: QueryFactory<DeploymentsArgs, GenesisTypes.Deployment[]>;
}

interface SitesResource {
  list: QueryFactory<void, GenesisTypes.GenesisSiteJson[]>;
  status: QueryFactory<
    { site_mongo_id: string },
    GenesisTypes.GenesisSiteStatusJson[]
  >;
}

interface BellResource {
  proposeOptimization: QueryFactory<
    void,
    GenesisTypes.BellProposeOptimizationResponse
  >;
  optimize: () => Promise<GenesisTypes.BellOptimizeResponse>;
}

interface GenesisAPIContextValue {
  trips: FieldTripsResource;
  fieldNotes: FieldNotesResource;
  photos: PhotosResource;
  wellInfos: FieldWellInfosResource;
  wellTypes: LookupResource;
  wellPowerTypes: LookupResource;
  issueTypes: LookupResource;
  distributorInjectionTypes: LookupResource;
  distributorWaterTypes: LookupResource;
  distributors: DistributorsResource;
  labels: LabelsResource;
  quantifiers: GenesisQuantifiersResource;
  deployments: DeploymentsResource;
  bell: BellResource;
  sites: SitesResource;
}

const GenesisAPIContext = createContext<GenesisAPIContextValue | null>(null);

type GenesisRequestInit =
  | { method: "GET" | "DELETE" }
  | { method: "POST" | "PUT"; body: unknown };

export function GenesisAPIProvider({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  const origin = useMemo(() => requireGenesisOrigin(DEFAULT_API_URL_V3), []);

  const genesisRequest = useCallback(
    async (url: string, init: GenesisRequestInit): Promise<Response> => {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token for Genesis API request");
      }
      const authHeaders: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };
      const res =
        init.method === "POST" || init.method === "PUT"
          ? await fetch(url, {
              method: init.method,
              headers: {
                ...authHeaders,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(init.body),
            })
          : await fetch(url, {
              method: init.method,
              headers: authHeaders,
            });
      if (!res.ok) await throwFromResponse(res, url);
      return res;
    },
    [getToken],
  );

  const genesisGetJson = useCallback(
    async (url: string): Promise<unknown> =>
      (await genesisRequest(url, { method: "GET" })).json(),
    [genesisRequest],
  );

  const genesisPostJson = useCallback(
    async (url: string, body: unknown): Promise<unknown> =>
      (await genesisRequest(url, { method: "POST", body })).json(),
    [genesisRequest],
  );

  const genesisPutJson = useCallback(
    async (url: string, body: unknown): Promise<unknown> =>
      (await genesisRequest(url, { method: "PUT", body })).json(),
    [genesisRequest],
  );

  const genesisDelete204 = useCallback(
    async (url: string): Promise<void> => {
      await genesisRequest(url, { method: "DELETE" });
    },
    [genesisRequest],
  );

  const genesisUploadToS3 = useCallback(
    async (uploadUrl: string, file: File): Promise<void> => {
      if (/^https?:\/\/\//.test(uploadUrl)) {
        throw new Error(
          "Photo upload URL is missing its S3 host. This is a server configuration issue with the presign endpoint.",
        );
      }
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!res.ok) {
        throw new Error(
          `Photo upload to S3 failed (${res.status}). The upload URL may have expired — try again.`,
        );
      }
    },
    [],
  );

  const ctx = useMemo<GenesisAPIContextValue>(
    () => ({
      trips: {
        list: queryFactory<TripListArgs, GenesisTypes.FieldTripListResult>(
          (args) => fieldTripsListUrl(origin, args),
          async (url) =>
            (await genesisGetJson(url)) as GenesisTypes.FieldTripIndexResponse,
        ),
        show: queryFactory<{ id: string }, GenesisTypes.FieldTripJson>(
          ({ id }) =>
            genesisUrl(origin, `/api/field/trips/${encodeURIComponent(id)}`),
          async (url) =>
            ((await genesisGetJson(url)) as GenesisTypes.FieldTripShowResponse)
              .data,
        ),
        create: async (body: GenesisTypes.FieldTripCreateBody) =>
          (
            (await genesisPostJson(
              genesisUrl(origin, "/api/field/trips"),
              body,
            )) as GenesisTypes.FieldTripShowResponse
          ).data,
        update: async (id: string, body: GenesisTypes.FieldTripUpdateBody) =>
          (
            (await genesisPutJson(
              genesisUrl(origin, `/api/field/trips/${encodeURIComponent(id)}`),
              body,
            )) as GenesisTypes.FieldTripShowResponse
          ).data,
        delete: (id: string) =>
          genesisDelete204(
            genesisUrl(origin, `/api/field/trips/${encodeURIComponent(id)}`),
          ),
      },
      photos: {
        presign: async (body: GenesisTypes.PhotoPresignBody) =>
          (
            (await genesisPostJson(
              genesisUrl(origin, "/api/field/photos/presign"),
              body,
            )) as GenesisTypes.PhotoPresignResponse
          ).data,
        upload: async (
          file: File,
          scope: GenesisTypes.PhotoScope,
          siteMongoId: string,
        ) => {
          const { path, upload_url } = (
            (await genesisPostJson(
              genesisUrl(origin, "/api/field/photos/presign"),
              {
                scope,
                site_mongo_id: siteMongoId,
                filename: file.name,
                content_type: file.type,
              } satisfies GenesisTypes.PhotoPresignBody,
            )) as GenesisTypes.PhotoPresignResponse
          ).data;
          await genesisUploadToS3(upload_url, file);
          return path;
        },
      },
      fieldNotes: {
        list: queryFactory<NoteListArgs, GenesisTypes.FieldNoteListResult>(
          (args) => fieldNotesListUrl(origin, args),
          async (url) =>
            (await genesisGetJson(url)) as GenesisTypes.FieldNoteIndexResponse,
        ),
        show: queryFactory<{ id: string }, GenesisTypes.FieldNoteJson>(
          ({ id }) =>
            genesisUrl(origin, `/api/field/notes/${encodeURIComponent(id)}`),
          async (url) =>
            ((await genesisGetJson(url)) as GenesisTypes.FieldNoteShowResponse)
              .data,
        ),
        create: async (body: GenesisTypes.FieldNoteCreateBody) =>
          (
            (await genesisPostJson(
              genesisUrl(origin, "/api/field/notes"),
              body,
            )) as GenesisTypes.FieldNoteShowResponse
          ).data,
        update: async (id: string, body: GenesisTypes.FieldNoteUpdateBody) =>
          (
            (await genesisPutJson(
              genesisUrl(origin, `/api/field/notes/${encodeURIComponent(id)}`),
              body,
            )) as GenesisTypes.FieldNoteShowResponse
          ).data,
        delete: (id: string) =>
          genesisDelete204(
            genesisUrl(origin, `/api/field/notes/${encodeURIComponent(id)}`),
          ),
      },
      wellInfos: {
        list: queryFactory<SiteFilterArgs, GenesisTypes.FieldWellInfoJson[]>(
          (args) => wellInfosListUrl(origin, args),
          async (url) =>
            (
              (await genesisGetJson(
                url,
              )) as GenesisTypes.FieldWellInfoIndexResponse
            ).data,
        ),
        show: queryFactory<{ id: string }, GenesisTypes.FieldWellInfoJson>(
          ({ id }) =>
            genesisUrl(
              origin,
              `/api/field/well-infos/${encodeURIComponent(id)}`,
            ),
          async (url) =>
            (
              (await genesisGetJson(
                url,
              )) as GenesisTypes.FieldWellInfoShowResponse
            ).data,
        ),
        create: async (body: GenesisTypes.FieldWellInfoCreateBody) =>
          (
            (await genesisPostJson(
              genesisUrl(origin, "/api/field/well-infos"),
              body,
            )) as GenesisTypes.FieldWellInfoShowResponse
          ).data,
        update: async (
          id: string,
          body: GenesisTypes.FieldWellInfoUpdateBody,
        ) =>
          (
            (await genesisPutJson(
              genesisUrl(
                origin,
                `/api/field/well-infos/${encodeURIComponent(id)}`,
              ),
              body,
            )) as GenesisTypes.FieldWellInfoShowResponse
          ).data,
        delete: (id: string) =>
          genesisDelete204(
            genesisUrl(
              origin,
              `/api/field/well-infos/${encodeURIComponent(id)}`,
            ),
          ),
      },
      sites: {
        list: queryFactory<void, GenesisTypes.GenesisSiteJson[]>(
          genesisUrl(origin, "/api/sites"),
          async (url) =>
            (
              (await genesisGetJson(
                url,
              )) as GenesisTypes.GenesisSiteIndexResponse
            ).data,
        ),
        status: queryFactory<
          { site_mongo_id: string },
          GenesisTypes.GenesisSiteStatusJson[]
        >(
          ({ site_mongo_id }) =>
            genesisUrl(
              origin,
              `/api/sites/${encodeURIComponent(site_mongo_id)}/status`,
            ),
          async (url) =>
            (
              (await genesisGetJson(
                url,
              )) as GenesisTypes.GenesisSiteStatusResponse
            ).data,
        ),
      },
      wellTypes: buildLookupSegment(
        origin,
        "well-types",
        genesisGetJson,
        genesisPostJson,
        genesisPutJson,
        genesisDelete204,
      ),
      wellPowerTypes: buildLookupSegment(
        origin,
        "well-power-types",
        genesisGetJson,
        genesisPostJson,
        genesisPutJson,
        genesisDelete204,
      ),
      issueTypes: buildLookupSegment(
        origin,
        "issue-types",
        genesisGetJson,
        genesisPostJson,
        genesisPutJson,
        genesisDelete204,
      ),
      distributorInjectionTypes: buildLookupSegment(
        origin,
        "distributor-injection-types",
        genesisGetJson,
        genesisPostJson,
        genesisPutJson,
        genesisDelete204,
      ),
      distributorWaterTypes: buildLookupSegment(
        origin,
        "distributor-water-types",
        genesisGetJson,
        genesisPostJson,
        genesisPutJson,
        genesisDelete204,
      ),
      distributors: {
        list: queryFactory<
          DistributorListArgs,
          GenesisTypes.DistributorListResult
        >(
          (args) => distributorsListUrl(origin, args),
          async (url) =>
            (await genesisGetJson(
              url,
            )) as GenesisTypes.DistributorIndexResponse,
        ),
        show: queryFactory<{ id: string }, GenesisTypes.DistributorJson>(
          ({ id }) =>
            genesisUrl(
              origin,
              `/api/field/distributors/${encodeURIComponent(id)}`,
            ),
          async (url) =>
            (
              (await genesisGetJson(
                url,
              )) as GenesisTypes.DistributorShowResponse
            ).data,
        ),
        create: async (body: GenesisTypes.DistributorWriteBody) =>
          (
            (await genesisPostJson(
              genesisUrl(origin, "/api/field/distributors"),
              body,
            )) as GenesisTypes.DistributorShowResponse
          ).data,
        update: async (
          id: string,
          body: Partial<GenesisTypes.DistributorWriteBody>,
        ) =>
          (
            (await genesisPutJson(
              genesisUrl(
                origin,
                `/api/field/distributors/${encodeURIComponent(id)}`,
              ),
              body,
            )) as GenesisTypes.DistributorShowResponse
          ).data,
        delete: (id: string) =>
          genesisDelete204(
            genesisUrl(
              origin,
              `/api/field/distributors/${encodeURIComponent(id)}`,
            ),
          ),
        parameters: {
          list: queryFactory<
            { distributorId: string },
            GenesisTypes.DistributorParameterJson[]
          >(
            ({ distributorId }) =>
              genesisUrl(
                origin,
                `/api/field/distributors/${encodeURIComponent(distributorId)}/parameters`,
              ),
            async (url) =>
              (
                (await genesisGetJson(
                  url,
                )) as GenesisTypes.DistributorParameterIndexResponse
              ).data,
          ),
          show: queryFactory<
            { distributorId: string; id: string },
            GenesisTypes.DistributorParameterJson
          >(
            ({ distributorId, id }) =>
              genesisUrl(
                origin,
                `/api/field/distributors/${encodeURIComponent(distributorId)}/parameters/${encodeURIComponent(id)}`,
              ),
            async (url) =>
              (
                (await genesisGetJson(
                  url,
                )) as GenesisTypes.DistributorParameterShowResponse
              ).data,
          ),
          create: async (
            distributorId: string,
            body: GenesisTypes.DistributorParameterWriteBody,
          ) =>
            (
              (await genesisPostJson(
                genesisUrl(
                  origin,
                  `/api/field/distributors/${encodeURIComponent(distributorId)}/parameters`,
                ),
                body,
              )) as GenesisTypes.DistributorParameterShowResponse
            ).data,
          update: async (
            distributorId: string,
            id: string,
            body: GenesisTypes.DistributorParameterWriteBody,
          ) =>
            (
              (await genesisPutJson(
                genesisUrl(
                  origin,
                  `/api/field/distributors/${encodeURIComponent(distributorId)}/parameters/${encodeURIComponent(id)}`,
                ),
                body,
              )) as GenesisTypes.DistributorParameterShowResponse
            ).data,
          delete: (distributorId: string, id: string) =>
            genesisDelete204(
              genesisUrl(
                origin,
                `/api/field/distributors/${encodeURIComponent(distributorId)}/parameters/${encodeURIComponent(id)}`,
              ),
            ),
        },
      },
      labels: {
        find: async (name: string) => {
          const url = genesisUrl(
            origin,
            `/api/labels/find?name=${encodeURIComponent(name)}`,
          );
          return ((await genesisGetJson(url)) as GenesisTypes.LabelFindResponse)
            .data;
        },
      },
      quantifiers: {
        create: async (
          siteId: string,
          body: GenesisTypes.GenesisCreateQuantifierBody,
        ) => {
          const url = genesisUrl(
            origin,
            `/api/sites/${encodeURIComponent(siteId)}/quantifiers`,
          );
          return (
            (await genesisPostJson(
              url,
              body,
            )) as GenesisTypes.GenesisCreateQuantifierResponse
          ).data;
        },
      },
      deployments: {
        list: queryFactory<DeploymentsArgs, GenesisTypes.Deployment[]>(
          (args) => deploymentsListUrl(origin, args),
          async (url) =>
            (
              (await genesisGetJson(
                url,
              )) as GenesisTypes.DeploymentsIndexResponse
            ).data,
        ),
      },
      bell: {
        proposeOptimization: queryFactory<
          void,
          GenesisTypes.BellProposeOptimizationResponse
        >(
          genesisUrl(origin, "/api/bell/propose_optimization"),
          async (url) =>
            (await genesisGetJson(
              url,
            )) as GenesisTypes.BellProposeOptimizationResponse,
        ),
        optimize: async () =>
          (await genesisPostJson(
            genesisUrl(origin, "/api/bell/optimize"),
            {},
          )) as GenesisTypes.BellOptimizeResponse,
      },
    }),
    [
      origin,
      genesisGetJson,
      genesisPostJson,
      genesisPutJson,
      genesisDelete204,
      genesisUploadToS3,
    ],
  );

  return (
    <GenesisAPIContext.Provider value={ctx}>
      {children}
    </GenesisAPIContext.Provider>
  );
}

export function useGenesisAPI() {
  const context = useContext(GenesisAPIContext);
  if (!context) {
    throw new Error("useGenesisAPI must be used within a GenesisAPIProvider");
  }
  return context;
}
